import { AuthGuard, hasProspectingWithoutCrm, useAuth } from '@commander/auth';

import { useFeatureFlags } from '@commander/feature-flags';
import { useAnalyticsIdentification } from '@commander/product-analytics';
import { TrialExpiredScreen } from '@commander/ui';
import { openTrialUpgrade, useTrialStatus, useUserDetails } from '@commander/user-details';
import { appRegistry } from '@commander/utils';
import { lazy, type ReactNode, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { LazyBoundary, lazyWithRetry } from './components/LazyBoundary';
import { recordFirstRoute } from './features/action-dash/analytics';
import { MegaModeLanding } from './features/megamode/MegaModeLanding';
import { RealtimeNotificationsProvider } from './features/notifications/RealtimeNotificationsProvider';
import { OnboardingPage } from './features/onboarding/pages';
import { ShellLayout } from './layouts/ShellLayout';
// Eager (not lazy): this is the external-signer landing page (e-sign redirect
// target). Bundling it into the main chunk avoids a first-load blank if the lazy
// chunk isn't compiled/available yet — reliability matters most on this page.
import EsignComplete from './pages/EsignComplete';

// Lazy load pages for code splitting￼View Analytics
const Login = lazy(() => import('./pages/Login'));
const SignUp = lazy(() => import('./pages/SignUp'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const SessionExpired = lazy(() => import('./pages/SessionExpired'));
const VerifyForgotPassword = lazy(() => import('./pages/VerifyForgotPassword'));
const InviteUserToWorkspace = lazy(() => import('./pages/InviteUserToWorkspace'));
const LoginVerification = lazy(() => import('./pages/LoginVerification'));
const VerifySignup = lazy(() => import('./pages/VerifySignup'));
const CheckEmail = lazy(() => import('./pages/CheckEmail'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Apps = lazy(() => import('./pages/Apps'));
const Notifications = lazy(() => import('./features/notifications/NotificationsPage'));
const ProfileSettings = lazy(() => import('./pages/ProfileSettings'));
const PartnerApiDocsPage = lazy(() => import('./pages/PartnerApiDocsPage'));
const ZapierDocsPage = lazy(() => import('./pages/ZapierDocsPage'));
const SearchResultsPage = lazy(() => import('./features/search/pages/SearchResultsPage'));
const TestShadcn = lazy(() => import('./TestShadcn'));
const AppDebug = lazy(() => import('./pages/AppDebug'));
const DocuSignCallback = lazy(() => import('./pages/DocuSignCallback'));
const DocuSignReferralLogin = lazy(() => import('./pages/DocuSignReferralLogin'));
const MobileAuthCallback = lazy(() => import('./pages/MobileAuthCallback'));

// Account management feature (lazy loaded)
const AccountPage = lazy(() => import('./features/account/pages/AccountPage'));
const ApiAccessPage = lazy(() => import('./features/account/pages/ApiAccess/ApiAccessPage'));
const AccountAdminGuard = lazy(() =>
  import('./features/account/components/AdminGuard').then((m) => ({
    default: m.AdminGuard,
  }))
);

// Territory management feature (lazy loaded)
const TerritoriesPage = lazy(() => import('./features/territories/pages/TerritoriesPage'));

// Commission tracking feature (lazy loaded)
const CommissionDashboardPage = lazy(
  () => import('./features/commissions/pages/CommissionDashboardPage')
);
const CommissionAccessDeniedPage = lazy(
  () => import('./features/commissions/pages/CommissionAccessDeniedPage')
);

// Global Search v2 (lazy loaded)
const GlobalSearchPage = lazy(() => import('./features/global-search/pages/GlobalSearchPage'));
const GlobalSearchHistoryPage = lazy(
  () => import('./features/global-search/pages/GlobalSearchHistoryPage')
);
const GlobalSearchQueuePage = lazy(
  () => import('./features/global-search/pages/GlobalSearchQueuePage')
);

// HaulerCentral Quotes feature (lazy loaded)
const QuotesListPage = lazy(() =>
  import('./features/haulercentral/pages/QuotesListPage').then((m) => ({
    default: m.QuotesListPage,
  }))
);
const QuotesDetailPage = lazy(() =>
  import('./features/haulercentral/pages/QuotesDetailPage').then((m) => ({
    default: m.QuotesDetailPage,
  }))
);

// Lazy load micro-apps (only in production)
// Import App.tsx directly from source for monolithic build (like Kulcare)
// In development, apps run on separate ports so we don't load them
const CRMApp = import.meta.env.PROD ? lazyWithRetry(() => import('../../crm/src/App')) : null;

const ProspectingApp = import.meta.env.PROD
  ? lazyWithRetry(() => import('../../prospecting/src/App'))
  : null;

const SuccessApp = import.meta.env.PROD
  ? lazyWithRetry(() => import('../../success/src/App'))
  : null;

const RoutingApp = import.meta.env.PROD
  ? lazyWithRetry(() => import('../../routing/src/App'))
  : null;
const DriverRoutingApp = import.meta.env.PROD
  ? lazyWithRetry(() => import('../../driver-routing/src/App'))
  : null;

console.log('[Shell] App module loaded, PROD mode:', import.meta.env.PROD, {
  hasProspectingApp: !!ProspectingApp,
  hasSuccessApp: !!SuccessApp,
  hasCRMApp: !!CRMApp,
});

// Auth routes wrapper - only accessible when NOT authenticated.
// `?form=1` bypasses the redirect, for previewing these pages with an active
// session (e.g. QA) without clearing cookies/local storage.
function AuthRoutes({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();

  if (isAuthenticated && searchParams.get('form') !== '1') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

/**
 * Signals alert links (bell and email) point at `/signals/opportunities/:id?alert=:alertId`.
 * The opportunity detail view lives in both the CRM and the prospecting app; the link is
 * forwarded, query kept, to the prospecting app for a user with Prospecting but not CRM
 * access, and to the CRM for everyone else (a signed-out user lands on the CRM's login).
 * In local dev each app runs on its own origin, so the forward leaves the shell.
 */
function SignalsOpportunityRedirect() {
  const { id = '' } = useParams();
  const { search } = useLocation();
  const { user, isLoading } = useAuth();
  const appId = hasProspectingWithoutCrm(user?.company_employee?.app_access)
    ? 'prospecting'
    : 'crm';
  const target = appRegistry.getAppUrl(
    appId,
    `/signals/opportunities/${encodeURIComponent(id)}${search}`
  );
  const leavesShell = /^https?:\/\//.test(target);

  useEffect(() => {
    if (!isLoading && leavesShell) window.location.replace(target);
  }, [isLoading, leavesShell, target]);

  if (isLoading || leavesShell) return null;
  return <Navigate to={target} replace />;
}

/**
 * Records the route an authenticated session started on, for Action Dash's
 * `first_screen_of_session`. The production micro-app routes don't render
 * `ProtectedRoutes`, so they record here.
 */
function RecordFirstRoute({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  recordFirstRoute(pathname);
  return <>{children}</>;
}

// Protected routes wrapper - only accessible when authenticated
function ProtectedRoutes() {
  const { isAuthenticated, user, logout } = useAuth();
  const { flags, isLoading: isLoadingFlags } = useFeatureFlags();
  const { userDetails, isLoading: isLoadingUserDetails } = useUserDetails();
  const { isExpired } = useTrialStatus();
  const location = useLocation();
  const appAccess = user?.company_employee?.app_access || [];
  const isDriverOnlyUser = appAccess.length === 1 && appAccess[0] === 'driver';

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isDriverOnlyUser) {
    return <Navigate to="/driver-routing/" replace />;
  }

  // Flag-gated routes redirect with `replace` when their flag is off, so rendering them
  // before the flags load would permanently bounce a deep link to the fallback page.
  if (isLoadingUserDetails || isLoadingFlags) {
    return (
      <ShellLayout>
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading ...</p>
          </div>
        </div>
      </ShellLayout>
    );
  }

  // Hard lockout: an expired trial can only reach the Contact Sales screen.
  if (isExpired) {
    return <TrialExpiredScreen onUpgrade={openTrialUpgrade} onSignOut={logout} />;
  }

  // Show onboarding only when userDetails is loaded AND onboarding_complete is explicitly false
  const needsOnboarding = userDetails && !userDetails?.onboarding_complete;

  if (!needsOnboarding) {
    recordFirstRoute(location.pathname);
  }

  return (
    <>
      {needsOnboarding ? (
        <Routes>
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="*" element={<Navigate to="/onboarding" replace />} />
        </Routes>
      ) : (
        <ShellLayout>
          <RealtimeNotificationsProvider />
          <LazyBoundary>
            <Routes>
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/apps" element={<Apps />} />
              <Route path="/search" element={<SearchResultsPage />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/profile-settings" element={<ProfileSettings />} />
              <Route path="/debug" element={<AppDebug />} />
              <Route
                path="/account"
                element={
                  <AccountAdminGuard>
                    <AccountPage />
                  </AccountAdminGuard>
                }
              />
              <Route
                path="/account/api"
                element={
                  <AccountAdminGuard>
                    {flags.partner_api_docs ? (
                      <ApiAccessPage />
                    ) : (
                      <Navigate to="/account" replace />
                    )}
                  </AccountAdminGuard>
                }
              />
              <Route
                path="/docs/partner-api"
                element={
                  <AccountAdminGuard>
                    {flags.partner_api_docs ? (
                      <PartnerApiDocsPage />
                    ) : (
                      <Navigate to="/account" replace />
                    )}
                  </AccountAdminGuard>
                }
              />
              <Route
                path="/docs/zapier"
                element={
                  flags.zapier_integration ? (
                    <ZapierDocsPage />
                  ) : (
                    <Navigate to="/profile-settings" replace />
                  )
                }
              />
              <Route
                path="/territories"
                element={
                  <AccountAdminGuard>
                    <TerritoriesPage />
                  </AccountAdminGuard>
                }
              />
              <Route
                path="/commissions"
                element={
                  flags.commission_tracking ? (
                    <CommissionDashboardPage />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )
                }
              />
              <Route
                path="/commissions/access-denied"
                element={
                  flags.commission_tracking ? (
                    <CommissionAccessDeniedPage />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )
                }
              />
              <Route
                path="/global-search"
                element={
                  flags.global_search_v2 ? (
                    <GlobalSearchPage />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )
                }
              />
              <Route
                path="/global-search/history"
                element={
                  flags.global_search_v2 ? (
                    <GlobalSearchHistoryPage />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )
                }
              />
              <Route
                path="/global-search/queue"
                element={
                  flags.global_search_v2 ? (
                    <GlobalSearchQueuePage />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )
                }
              />
              <Route
                path="/profile/rfqs"
                element={
                  flags.haulercentral ? <QuotesListPage /> : <Navigate to="/dashboard" replace />
                }
              />
              <Route
                path="/profile/rfqs/:id"
                element={
                  flags.haulercentral ? <QuotesDetailPage /> : <Navigate to="/dashboard" replace />
                }
              />
            </Routes>
          </LazyBoundary>
        </ShellLayout>
      )}
    </>
  );
}

function App() {
  const { isAuthenticated } = useAuth();
  const { userDetails, isLoading } = useUserDetails();

  // Identify users in Mixpanel for analytics tracking
  useAnalyticsIdentification(isAuthenticated, userDetails, isLoading);

  return (
    <Routes>
      {/* Public, auth-free landing for external e-sign signers (e-sign redirect target). */}
      <Route path="/esign/complete" element={<EsignComplete />} />

      <Route
        path="/login"
        element={
          <AuthRoutes>
            <LazyBoundary>
              <Login />
            </LazyBoundary>
          </AuthRoutes>
        }
      />

      <Route
        path="/signup"
        element={
          <AuthRoutes>
            <LazyBoundary>
              <SignUp />
            </LazyBoundary>
          </AuthRoutes>
        }
      />

      <Route
        path="/check-email"
        element={
          <AuthRoutes>
            <LazyBoundary>
              <CheckEmail />
            </LazyBoundary>
          </AuthRoutes>
        }
      />

      <Route
        path="/forgot-password"
        element={
          <AuthRoutes>
            <LazyBoundary>
              <ForgotPassword />
            </LazyBoundary>
          </AuthRoutes>
        }
      />

      <Route
        path="/verify-forgot-password"
        element={
          <AuthRoutes>
            <LazyBoundary>
              <VerifyForgotPassword />
            </LazyBoundary>
          </AuthRoutes>
        }
      />

      <Route
        path="/invite-user-to-workspace"
        element={
          <AuthRoutes>
            <LazyBoundary>
              <InviteUserToWorkspace />
            </LazyBoundary>
          </AuthRoutes>
        }
      />

      <Route
        path="/login-verification"
        element={
          <AuthRoutes>
            <LazyBoundary>
              <LoginVerification />
            </LazyBoundary>
          </AuthRoutes>
        }
      />

      <Route
        path="/verify-signup"
        element={
          <AuthRoutes>
            <LazyBoundary>
              <VerifySignup />
            </LazyBoundary>
          </AuthRoutes>
        }
      />

      <Route
        path="/session-expired"
        element={
          <AuthRoutes>
            <LazyBoundary>
              <SessionExpired />
            </LazyBoundary>
          </AuthRoutes>
        }
      />

      <Route
        path="/test"
        element={
          <LazyBoundary>
            <TestShadcn />
          </LazyBoundary>
        }
      />

      {/* Admin-initiated megamode handoff - public route; establishes the
          megamode session from the token in the URL fragment. */}
      <Route path="/megamode" element={<MegaModeLanding />} />

      {/* DocuSign OAuth callback - opened in popup, no auth required */}
      <Route
        path="/auth/callback/docusign/success"
        element={
          <LazyBoundary>
            <DocuSignCallback />
          </LazyBoundary>
        }
      />

      {/* Mobile OAuth success/interstitial page — backend redirects here
          after a mobile-initiated Google/Microsoft login; the page then
          deep-links into the mobile app's custom URL scheme. Public. */}
      <Route
        path="/auth/callback/mobile"
        element={
          <LazyBoundary>
            <MobileAuthCallback />
          </LazyBoundary>
        }
      />

      {/* DocuSign Digital Referral landing (loginRedirectUri) - opened in the
          trial-signup popup, hands off into OAuth consent. Must match
          VITE_DOCUSIGN_REFERRAL_REDIRECT_URI. */}
      <Route
        path="/docusign/referral-login"
        element={
          <LazyBoundary>
            <DocuSignReferralLogin />
          </LazyBoundary>
        }
      />

      <Route path="/signals/opportunities/:id" element={<SignalsOpportunityRedirect />} />

      {/* Production-only routes for micro-apps */}
      {import.meta.env.PROD && CRMApp && (
        <Route
          path="/crm/*"
          element={
            <AuthGuard redirectTo="/login">
              <RecordFirstRoute>
                <LazyBoundary>
                  <CRMApp />
                </LazyBoundary>
              </RecordFirstRoute>
            </AuthGuard>
          }
        />
      )}

      {import.meta.env.PROD && ProspectingApp && (
        <Route
          path="/prospecting/*"
          element={
            <AuthGuard redirectTo="/login">
              <RecordFirstRoute>
                <LazyBoundary>
                  <ProspectingApp />
                </LazyBoundary>
              </RecordFirstRoute>
            </AuthGuard>
          }
        />
      )}

      {import.meta.env.PROD && SuccessApp && (
        <Route
          path="/success/*"
          element={
            <AuthGuard redirectTo="/login">
              <RecordFirstRoute>
                <LazyBoundary>
                  <SuccessApp />
                </LazyBoundary>
              </RecordFirstRoute>
            </AuthGuard>
          }
        />
      )}

      {import.meta.env.PROD && RoutingApp && (
        <Route
          path="/routing/*"
          element={
            <AuthGuard redirectTo="/login">
              <RecordFirstRoute>
                <LazyBoundary>
                  <RoutingApp />
                </LazyBoundary>
              </RecordFirstRoute>
            </AuthGuard>
          }
        />
      )}

      {import.meta.env.PROD && DriverRoutingApp && (
        <Route
          path="/driver-routing/*"
          element={
            <AuthGuard redirectTo="/login">
              <RecordFirstRoute>
                <LazyBoundary>
                  <DriverRoutingApp />
                </LazyBoundary>
              </RecordFirstRoute>
            </AuthGuard>
          }
        />
      )}

      <Route
        path="/*"
        element={
          <AuthGuard redirectTo="/login">
            <ProtectedRoutes />
          </AuthGuard>
        }
      />
    </Routes>
  );
}

export default App;
